import { api } from "@/lib/apiClient";

export type CompanyLedgerMode = "account_ledger" | "daily_vouchers";

export type CompanyMoneyCategory =
  | "money_received"
  | "money_paid"
  | "receivable"
  | "payable"
  | "expense";

export type CompanyLedgerLine = {
  id: number;
  date: string | null;
  voucher: string | null;
  voucher_type: string | null;
  category: CompanyMoneyCategory | null;
  category_label: string | null;
  account_id: number | null;
  account_code: string | null;
  account_name: string | null;
  party_id: number | null;
  party_name: string | null;
  product_id: number | null;
  product_name: string | null;
  particulars: string[];
  from_to: string | null;
  amount: number;
  money_in: number;
  money_out: number;
  direction: "in" | "out" | null;
  affects_cash: boolean;
  debit: number;
  credit: number;
  balance: number;
  balance_side: "Dr" | "Cr";
};

export type CompanyLedgerSummary = {
  money_received: number;
  money_paid: number;
  receivable: number;
  payable: number;
  expense: number;
};

export type CompanyLedger = {
  mode: CompanyLedgerMode;
  account: {
    id: number;
    code: string;
    name: string;
  } | null;
  filters: {
    account_id: number | null;
    date_from: string | null;
    date_to: string | null;
  };
  lines: CompanyLedgerLine[];
  receivable_lines: CompanyLedgerLine[];
  summary: CompanyLedgerSummary | null;
  totals: {
    opening_balance: number;
    opening_balance_side: "Dr" | "Cr";
    debit: number;
    credit: number;
    balance: number;
    balance_side: "Dr" | "Cr";
    money_in: number;
    money_out: number;
  };
};

type ApiCompanyLedger = {
  mode?: string;
  account?: { id?: number; code?: string; name?: string } | null;
  filters?: {
    account_id?: number | null;
    date_from?: string | null;
    date_to?: string | null;
  };
  lines?: Array<Record<string, unknown>>;
  receivable_lines?: Array<Record<string, unknown>>;
  summary?: Partial<CompanyLedgerSummary> | null;
  totals?: {
    opening_balance?: number;
    opening_balance_side?: string;
    debit?: number;
    credit?: number;
    balance?: number;
    balance_side?: string;
    money_in?: number;
    money_out?: number;
  };
};

export type FetchCompanyLedgerParams = {
  accountId?: string | number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
};

export const COMPANY_MONEY_CATEGORY_META: Record<
  CompanyMoneyCategory,
  { label: string; tone: "in" | "out" | "neutral" }
> = {
  money_received: { label: "Amount Received", tone: "in" },
  money_paid: { label: "Amount Paid", tone: "out" },
  receivable: { label: "Pending Receivable", tone: "in" },
  payable: { label: "Payables", tone: "out" },
  expense: { label: "Expense", tone: "out" },
};

function ledgerQueryParams(params: FetchCompanyLedgerParams) {
  const accountId = params.accountId != null && String(params.accountId).trim() !== ""
    ? Number(params.accountId)
    : null;

  return {
    ...(accountId != null && accountId > 0 ? { account_id: accountId } : {}),
    ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
    ...(params.dateTo ? { date_to: params.dateTo } : {}),
  };
}

async function downloadLedgerBlob(url: string, params: FetchCompanyLedgerParams, filename: string): Promise<void> {
  const response = await api.get(url, {
    params: ledgerQueryParams(params),
    responseType: "blob",
  });
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function exportStamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export async function downloadCompanyLedgerPdf(params: FetchCompanyLedgerParams): Promise<void> {
  const isDaily = !(params.accountId != null && String(params.accountId).trim() !== "");
  const prefix = isDaily ? "company-ledger" : "general-ledger";
  await downloadLedgerBlob("/api/v1/accounts/company-ledger/pdf", params, `${prefix}-${exportStamp()}.pdf`);
}

export async function downloadCompanyLedgerExcel(params: FetchCompanyLedgerParams): Promise<void> {
  const isDaily = !(params.accountId != null && String(params.accountId).trim() !== "");
  const prefix = isDaily ? "company-ledger" : "general-ledger";
  await downloadLedgerBlob("/api/v1/accounts/company-ledger/excel", params, `${prefix}-${exportStamp()}.csv`);
}

function asSide(value: unknown): "Dr" | "Cr" {
  return String(value ?? "").toLowerCase() === "cr" ? "Cr" : "Dr";
}

function asMode(value: unknown): CompanyLedgerMode {
  return String(value ?? "") === "daily_vouchers" ? "daily_vouchers" : "account_ledger";
}

function asCategory(value: unknown): CompanyMoneyCategory | null {
  const key = String(value ?? "");
  if (key in COMPANY_MONEY_CATEGORY_META) return key as CompanyMoneyCategory;
  return null;
}

function asDirection(value: unknown, category: CompanyMoneyCategory | null, moneyIn: number, moneyOut: number): "in" | "out" | null {
  if (value === "in" || value === "out") return value;
  if (moneyIn > 0) return "in";
  if (moneyOut > 0) return "out";
  if (category === "money_received" || category === "receivable") return "in";
  if (category === "money_paid" || category === "payable" || category === "expense") return "out";
  return null;
}

function mapLine(row: Record<string, unknown>): CompanyLedgerLine {
  const particularsRaw = row.particulars;
  const particulars = Array.isArray(particularsRaw)
    ? particularsRaw.map((p) => String(p)).filter(Boolean)
    : typeof particularsRaw === "string" && particularsRaw.trim()
      ? [particularsRaw.trim()]
      : [];

  const category = asCategory(row.category);
  const debit = Number(row.debit ?? 0);
  const credit = Number(row.credit ?? 0);
  const moneyIn = Number(row.money_in ?? debit ?? 0);
  const moneyOut = Number(row.money_out ?? credit ?? 0);
  const amount = Number(row.amount ?? Math.max(moneyIn, moneyOut));

  return {
    id: Number(row.id ?? 0),
    date: (row.date as string | null) ?? null,
    voucher: (row.voucher as string | null) ?? null,
    voucher_type: (row.voucher_type as string | null) ?? null,
    category,
    category_label:
      (row.category_label as string | null)
      ?? (category ? COMPANY_MONEY_CATEGORY_META[category].label : null),
    account_id: row.account_id != null ? Number(row.account_id) : null,
    account_code: (row.account_code as string | null) ?? null,
    account_name: (row.account_name as string | null) ?? null,
    party_id: row.party_id != null ? Number(row.party_id) : null,
    party_name: (row.party_name as string | null) ?? null,
    product_id: row.product_id != null ? Number(row.product_id) : null,
    product_name: (row.product_name as string | null) ?? null,
    particulars,
    from_to: (row.from_to as string | null) ?? null,
    amount,
    money_in: moneyIn,
    money_out: moneyOut,
    direction: asDirection(row.direction, category, moneyIn, moneyOut),
    affects_cash: row.affects_cash !== false && category !== "receivable",
    debit,
    credit,
    balance: Number(row.balance ?? 0),
    balance_side: asSide(row.balance_side),
  };
}

export async function fetchCompanyLedger(params: FetchCompanyLedgerParams): Promise<CompanyLedger> {
  const response = await api.get<{ data: ApiCompanyLedger }>("/api/v1/accounts/company-ledger", {
    params: ledgerQueryParams(params),
  });
  const data = response.data?.data ?? {};
  const account = data.account;
  const summary = data.summary;
  return {
    mode: asMode(data.mode),
    account: account?.id
      ? {
          id: Number(account.id),
          code: account.code ?? "",
          name: account.name ?? "",
        }
      : null,
    filters: {
      account_id: data.filters?.account_id != null ? Number(data.filters.account_id) : null,
      date_from: data.filters?.date_from ?? null,
      date_to: data.filters?.date_to ?? null,
    },
    lines: Array.isArray(data.lines) ? data.lines.map(mapLine) : [],
    receivable_lines: Array.isArray(data.receivable_lines) ? data.receivable_lines.map(mapLine) : [],
    summary: summary
      ? {
          money_received: Number(summary.money_received ?? 0),
          money_paid: Number(summary.money_paid ?? 0),
          receivable: Number(summary.receivable ?? 0),
          payable: Number(summary.payable ?? 0),
          expense: Number(summary.expense ?? 0),
        }
      : null,
    totals: {
      opening_balance: Number(data.totals?.opening_balance ?? 0),
      opening_balance_side: asSide(data.totals?.opening_balance_side),
      debit: Number(data.totals?.debit ?? 0),
      credit: Number(data.totals?.credit ?? 0),
      balance: Number(data.totals?.balance ?? 0),
      balance_side: asSide(data.totals?.balance_side),
      money_in: Number(data.totals?.money_in ?? data.totals?.debit ?? 0),
      money_out: Number(data.totals?.money_out ?? data.totals?.credit ?? 0),
    },
  };
}

export function formatLedgerAccountLabel(line: Pick<CompanyLedgerLine, "account_code" | "account_name">): string {
  const code = (line.account_code ?? "").trim();
  const name = (line.account_name ?? "").trim();
  if (code && name) return `${code} — ${name}`;
  return name || code || "—";
}
